import { useEffect, useState } from 'react';

/**
 * Returns a debounced view of `value` that updates only after `delayMs` of
 * stability. Rapid changes within the window collapse — only the LAST value
 * surfaces. Cleanup on unmount cancels any pending update.
 *
 * Use cases:
 *   - Sync a typed search input to URL state without per-keystroke fan-out.
 *   - Throttle expensive `useEffect` work tied to a fast-changing prop.
 *
 * Sibling helpers in `./debouncer.ts`:
 *   - `createDebouncer<T>(emit, delayMs)` — pure (React-free) timer used here
 *     and unit-tested in isolation.
 *   - `effectiveSearch(raw)` — strips whitespace + enforces 2-char minimum,
 *     returning `undefined` for sub-minimum input so callers skip the fetch.
 *
 * Different from `useDebouncedSearch` in this folder, which is for combobox
 * typeahead with an async fetcher. `useDebouncedValue` is a generic value
 * debouncer that pairs well with TanStack Query (queryKey-driven cancellation).
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
